import { Component, Injectable, Type } from "@angular/core";
import { ISPagination } from "../models/api";
import { AlignType, ColumnType, FilterOperator, FilterOptionsType, SortDirection } from "../models/generic";
import { DialogConfig } from "../is-dialog/is-dialog-config";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { Observable, Subject } from "rxjs";
import { FormControl } from "@angular/forms";

export class ISTableDatasource {
    public isFetching: boolean = false;
    constructor(
        public columns: ISColumn[],
        public columnBtns: ISColumnBtn[],
        public body?: any[],
        public filterRow: boolean = true,
        public pagination: ISPagination = { pageSize: 10, page: 1 },
        public totalItems: number = 0,
        public apiUrl: string | undefined = undefined,
        public headerFilters?: ISColumn[],
        public selectableRows: ISSelectableRowsOptions = { show: false, selectMultipleRows: false },
        public hideFilters?: boolean,
        public disableOrder?: boolean,
    ) { }
}

export class ISColumn {
    constructor(
        public identifier: string,
        public title: string,
        public filterOptions: FilterOptions = { value: '', type: FilterOptionsType.text, operator: FilterOperator.contains },
        public type: ColumnType | ColumnTypeConfig = ColumnType.text,
        public width: string = 'auto',
        public align: AlignType = AlignType.left,
        public direction: SortDirection = SortDirection.none,
        public options: ISSelectOption[] = [],
        public labelOptions: ISLabelOptions[] = [],
        public prefix?: string,
        public suffix?: string,
        public confidencialContent?: ISConfidencialColumnOptions,
        public applyPipe: boolean = true,
        public alternativeIdentifier?: string,
        public subscript?: string,
        public linkOptions?: LinkOptions,
        public show: boolean = true,
        public isParentObject: boolean = false,
        public conditionalFunction: ((item: any) => any) | null = null,

        /** Static tooltip text (usually a translation key). */
        public tooltipText?: string,

        /** Dynamic tooltip (wins over tooltipText if present). */
        public tooltipFn?: (item: any) => string,

        /** Optional: read tooltip from a field in the item (wins over tooltipText, loses to tooltipFn). */
        public tooltipField?: string,
        public tooltipDisableWhenEmpty: boolean = true
    ) { }
}

export class ISColumnToShow {
    constructor(
        public identifier: string,
        public title: string,
        public filterOptions: FilterOptions = { value: '', type: FilterOptionsType.text, operator: FilterOperator.contains },
        public type: ColumnTypeConfig,
        public width: string = 'auto',
        public align: AlignType = AlignType.left,
        public direction: SortDirection = SortDirection.none,
        public options: ISSelectOption[] = [],
        public labelOptions: ISLabelOptions[] = [],
        public prefix?: string,
        public suffix?: string,
        public confidencialContent?: ISConfidencialColumnOptions,
        public applyPipe: boolean = true,
        public alternativeIdentifier?: string,
        public subscript?: string,
        public linkOptions?: LinkOptions,
        public show: boolean = true,
        public isParentObject: boolean = false,
        public conditionalFunction: ((item: any) => any) | null = null,

        /** Static tooltip text (usually a translation key). */
        public tooltipText?: string,

        /** Dynamic tooltip (wins over tooltipText if present). */
        public tooltipFn?: (item: any) => string,

        /** Optional: read tooltip from a field in the item (wins over tooltipText, loses to tooltipFn). */
        public tooltipField?: string,
        public tooltipDisableWhenEmpty: boolean = true
    ) { }
}

export class ISLabelOptions {
    constructor(
        public value: any,
        public label: string,
        public style: string,
    ) { }
}

export class ISConfidencialColumnOptions {
    constructor(
        public startCharsToHide: number = 0,
        public middleCharsToHide: number = 0,
        public endCharsToHide: number = 0,
    ) { }
}

export class ISSelectableRowsOptions {
    constructor(
        public show: boolean,
        public selectMultipleRows: boolean,
        public rowsToKeep?: ISRowsToKeepData[]
    ) { }
}

export class ISRowsToKeepData {
    constructor(
        public id: any
    ) { }
}

export class ISSelectOption {
    constructor(
        public value: any,
        public label: string,
        public icon?: string,
        public style?: string
    ) { }
}

export class ISColumnBtn {
    constructor(
        public type: ButtonType,
        public styling: string,
        public icon: string,
        public childrens?: ISColumnChildrenBtn[],
        public tooltip?: string,
    ) { }
}

export class ISColumnChildrenBtn {
    constructor(
        public type: ButtonType,
        public styling: string,
        public icon: string,
        public tooltip?: string,
    ) { }
}

export class ISSelectedRow {
    constructor(
        public id: number,
        public index: number,
        public rowData: string,
    ) { }
}

export class FilterOptions {
    constructor(
        public value: any,
        public type: FilterOptionsType = FilterOptionsType.text,
        public operator: FilterOperator = FilterOperator.contains,
        public navigation?: string,
        public searchField?: string,
        public alternativeIdentifier?: string,
    ) { }
}

export class ISChipSetDatasource {
    constructor(
        public displayName: string,
        public filterName: string,
        public labelColumn?: string,
        public displayString?: string
    ) { }
}

export class ISChipSetData {
    constructor(
        public displayName: string,
        public filterName: string,
        public displayString: string
    ) { }
}

export class LinkOptions {
    /**
     * Configuração do link
     * @param type Tipo do link (dialog, route, url)
     * @param url Url para o qual navegar (tipos: route e url)
     * - nomenclatura de variáveis: $!{index}; exemplo: 'entities/client/$!0'
     * @param dialogConfig Configuração do dialog a evocar (tipos: dialog)
     * @param dialogComponent Componente com o qual preencher o dialog (tipos: dialog)
     * @param openNewPage Se ao navegar para um url deve abrir uma nova página (tipos: route e url)
     * @param recordIdentifiers Vetor com os dados a ser usados para preencher as variáveis do url/dados do dialog (variáveis têm de corresponder ao nome de um campo que seja devolvido pelo DTO)
     */
    constructor(
        public type: LinkType,
        public url?: string | undefined,
        public dialogConfig?: DialogConfig | undefined,
        public dialogComponent?: Type<any>,
        public openNewPage: boolean = false,
        public recordIdentifiers: string[] = [],
    ) { }
}

export class ColumnTypeConfig {
    constructor(
        public type: ColumnType,
        public format?: string,
        public timezone?: string,
        public currencyCode?: string,
    ) { }
}

export enum ButtonType {
    edit = 'edit',
    delete = 'delete',
    update = 'update',
    view = 'view',
    custom = 'custom',
    approve = 'approve',
    decline = 'decline',
    popupView = 'popupView',
    externalView = 'externalView'
}

/**
 * Dialog - Representa um link que abre um dialog
 * Route - Representa um link que aponta para uma rota do Angular
 * Url - Representa um link que aponta para um URL externo
 */
export enum LinkType {
    dialog = "dialog",
    route = "route",
    url = "url",
    custom = "custom"
}

@Injectable({
    providedIn: 'root'
})
export class PaginationService implements MatPaginatorIntl {
    changes: Subject<void> = new Subject<void>();
    itemsPerPageLabel: string = '';
    nextPageLabel: string = '';
    previousPageLabel: string = '';
    firstPageLabel: string = '';
    lastPageLabel: string = '';
    getRangeLabel = (page: number, pageSize: number, length: number): string => { return ''; };

    setLanguageStream(stream: Observable<string | any>) {
        stream.subscribe((result: string | any) => {
            this.itemsPerPageLabel = result['lib.ITEMS_PER_PAGE'];
            this.nextPageLabel = result['lib.NEXT_PAGE_LABEL'];
            this.previousPageLabel = result['lib.PREVIOUS_PAGE_LABEL'];
            this.firstPageLabel = result['lib.FIRST_PAGE_LABEL'];
            this.lastPageLabel = result['lib.LAST_PAGE_LABEL'];
            this.getRangeLabel = (page: number, pageSize: number, length: number) => { return `${page * pageSize + 1} - ${page * pageSize + pageSize} ${result["lib.OF"]} ${length}`; };
        });
    }
}